import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-date-picker';
import moment from 'moment';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import CalendarRefreshOutlineIcon from 'mdi-react/CalendarRefreshOutlineIcon';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import CloseCircleIcon from 'mdi-react/CloseCircleIcon';
import StyleConstants from '../../../core/styles.constants';
import notify from '../../../core/components/notify/notify.component';
import type { WorkplaceReservation } from '../services/floorplan-editor.service';
import { useWorkplaceReservations } from '../hooks/use-workplace-reservations';
import { reservationsToClipboardText } from '../utils/reservations-clipboard';
import datePickerClass from './workplace-reservations-dialog.styles';

/**
 * Off-screen but still read aloud. Spelled out here rather than pulled from `@mui/utils`,
 * which is a transitive dependency this project does not declare.
 */
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/**
 * How tall the table gets before it scrolls instead of the dialog.
 *
 * A custom range can be a year wide, and a dialog that grows past the viewport puts its own
 * Close button below the fold. The header stays pinned so a scrolled row still has columns.
 *
 * Capped against the viewport as well as in pixels, because the dialog no longer has a scroll
 * box of its own — that is what used to clip the calendar — so this is the only thing keeping
 * a long list inside a short window.
 */
const TABLE_MAX_HEIGHT = 'min(360px, 45vh)';

/** Midnight today, in the browser's own zone — the earliest day the pickers offer. */
const startOfToday = (): Date => {
  const today = new Date();

  today.setHours(0, 0, 0, 0);

  return today;
};

/** `yyyy-MM-dd`, which is what the endpoint's from/to expect. */
const asQueryDate = (date: Date): string => moment(date).format('YYYY-MM-DD');

interface Props {
  readonly open: boolean;
  /** The `Workplaces.Id` behind the selected desk. Undefined while nothing bookable is selected. */
  readonly workplaceId?: string;
  readonly onClose: () => void;
}

/** Green for held, red for not. Never colour alone — every cell carries the word too. */
const DayPartCell = ({ held, label }: { readonly held: boolean; readonly label: string }): React.JSX.Element => (
  <TableCell align="center">
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
      {held
        ? <CheckCircleIcon size={18} color={StyleConstants.LIGHT_GREEN_COLOR} aria-hidden />
        : <CloseCircleIcon size={18} color={StyleConstants.PINK_COLOR} aria-hidden />}
      {/* The tick and cross are the whole answer, and colour is the only thing separating
          them. A screen reader — and anyone who cannot tell the two colours apart — gets the
          word instead. */}
      <Box component="span" sx={VISUALLY_HIDDEN}>{label}</Box>
    </Stack>
  </TableCell>
);

/**
 * Who has this desk — the office's fill-in period by default, or any span of days from today
 * onwards that the administrator picks.
 *
 * The fill-in period is the opening question because it is the one the office's own setting
 * describes: those are the days employees can currently book. The pickers are there for the
 * two cases it does not cover — checking a single day next week, and finding reservations
 * further ahead than the window, which exist whenever the window was shortened after they
 * were made. Neither end can reach into the past: who *used* a desk is a different question
 * with different privacy weight, and the reporting exports answer it behind their own
 * controls.
 *
 * A dialog rather than a panel in the inspector: it is a table of people, it is read once to
 * answer a question ("who is sitting here on Thursday?"), and the inspector is a column of
 * controls that change the plan. Read-only throughout — reservations are made and cancelled
 * by the people who own them, and an editor that could quietly cancel someone's desk from a
 * drawing tool is a different, much riskier feature.
 */
const WorkplaceReservationsDialog = ({ open, workplaceId, onClose }: Props): React.JSX.Element => {
  const { t, i18n } = useTranslation();
  const [from, setFrom] = useState<Date | undefined>();
  const [to, setTo] = useState<Date | undefined>();

  // Both unset is what asks for the fill-in period, so the range is only sent once the admin
  // has actually picked something. Memoised on the two formatted strings, since a fresh object
  // every render would have the hook refetching the same days forever.
  const range = useMemo(
    () => ({
      from: from ? asQueryDate(from) : undefined,
      to: to ? asQueryDate(to) : undefined,
    }),
    [from, to],
  );

  const { loading, data, error } = useWorkplaceReservations(workplaceId, open, range);
  const custom = Boolean(from || to);

  const formatDate = (isoDate: string): string => new Date(isoDate).toLocaleDateString(i18n.language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    // The dates are calendar days stored at UTC midnight. Rendered in the browser's zone they
    // would slide a day backwards for anyone west of UTC.
    timeZone: 'UTC',
  });

  const nameOf = (reservation: WorkplaceReservation): string => {
    const name = [reservation.firstName, reservation.lastName].filter(Boolean).join(' ').trim();

    return name || String(t('floorplan-editor-reservations-unknown-user'));
  };

  /**
   * Copies the table as tab-separated rows.
   *
   * Offered because the answer to "who is at this desk next week" usually has somewhere else
   * to go — a mail to a facility manager, a column in a spreadsheet next to the room's other
   * desks — and retyping fourteen rows out of a dialog is how transcription errors get made.
   */
  const copy = async (): Promise<void> => {
    if (!data?.reservations.length) {
      return;
    }

    const text = reservationsToClipboardText(data.reservations, {
      date: String(t('floorplan-editor-reservations-date')),
      name: String(t('name')),
      email: String(t('email')),
      morning: String(t('morning')),
      afternoon: String(t('afternoon')),
      source: String(t('floorplan-editor-reservations-source')),
      booked: String(t('floorplan-editor-reservations-booked')),
      notBooked: String(t('floorplan-editor-reservations-not-booked')),
      unknownUser: String(t('floorplan-editor-reservations-unknown-user')),
      sourceReservation: String(t('floorplan-editor-reservations-source-reservation')),
      sourceGroup: String(t('floorplan-editor-reservations-source-group')),
      sourceCheckIn: String(t('floorplan-editor-reservations-source-check-in')),
    }, formatDate);

    try {
      await navigator.clipboard.writeText(text);
      notify(String(t('floorplan-editor-reservations-copied')), true);
    } catch {
      // Clipboard writes are refused outside a secure context and in some embedded browsers.
      // Saying so beats a button that appears to do nothing.
      notify(String(t('floorplan-editor-reservations-copy-failed')));
    }
  };

  // The default window is described as the office's fill-in period, because that is what it
  // is; a hand-picked range is not, and calling it one would misattribute the admin's own
  // dates to an office setting. Either way the dates come off the response, not off the
  // pickers — the server floors and caps what it was asked for.
  const windowLabel = data
    ? t(custom ? 'floorplan-editor-reservations-window-custom' : 'floorplan-editor-reservations-window', {
      from: formatDate(data.windowStart),
      to: formatDate(data.windowEnd),
      days: data.planPeriodDays,
    })
    : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      // The calendar is positioned absolutely next to its input, so any scroll box between it
      // and the viewport clips it — which is what happened here, worst on a short dialog where
      // there is nothing below to open over. Nothing needs a scroll box any more: the table
      // brings its own, so the dialog's height stays bounded whatever the range.
      PaperProps={{ sx: { overflowY: 'visible' } }}
    >
      <DialogTitle>{t('floorplan-editor-reservations-title')}</DialogTitle>
      <DialogContent sx={{ overflowY: 'visible' }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* The pickers sit above the answer rather than behind a "custom range" toggle: with
              two controls there is nothing to hide, and a toggle would make the common case
              (leave them alone) look like a mode. minDate is what refuses the past — the
              calendar simply will not offer an earlier day. */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ flexWrap: 'wrap' }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
                {t('floorplan-editor-reservations-from')}
              </Typography>
              <DatePicker
                format="dd/MM/yyyy"
                clearIcon={null}
                minDate={startOfToday()}
                value={from ?? null}
                onChange={(value): void => setFrom(value instanceof Date ? value : undefined)}
                // Only the root gets a class: the popup wrapper cannot be given one, so the
                // stylesheet reaches it from here. See the styles file.
                className={datePickerClass}
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
                {t('floorplan-editor-reservations-to')}
              </Typography>
              <DatePicker
                format="dd/MM/yyyy"
                clearIcon={null}
                // Never before the first day on show, so the pair cannot describe a span that
                // runs backwards.
                minDate={from ?? startOfToday()}
                value={to ?? null}
                onChange={(value): void => setTo(value instanceof Date ? value : undefined)}
                className={datePickerClass}
              />
            </Stack>

            {custom && (
              <Button
                size="small"
                startIcon={<CalendarRefreshOutlineIcon size={16} />}
                onClick={(): void => {
                  setFrom(undefined);
                  setTo(undefined);
                }}
              >
                {t('floorplan-editor-reservations-reset-range')}
              </Button>
            )}
          </Stack>

          {data && (
            <DialogContentText variant="body2">
              {data.roomName ? `${data.roomName} — ` : ''}{windowLabel}
            </DialogContentText>
          )}

          {loading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">{t('floorplan-editor-reservations-loading')}</Typography>
            </Stack>
          )}

          {error === 'forbidden' && (
            <Alert severity="warning">{t('floorplan-editor-reservations-forbidden')}</Alert>
          )}

          {error === 'notFound' && (
            <Alert severity="info">{t('floorplan-editor-reservations-not-bookable')}</Alert>
          )}

          {error === 'failed' && (
            <Alert severity="error">{t('floorplan-editor-reservations-failed')}</Alert>
          )}

          {!loading && !error && data?.reservations.length === 0 && (
            <Alert severity="info">{t('floorplan-editor-reservations-empty')}</Alert>
          )}

          {!loading && !error && !!data?.reservations.length && (
            <TableContainer sx={{ maxHeight: TABLE_MAX_HEIGHT }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('floorplan-editor-reservations-date')}</TableCell>
                    <TableCell>{t('name')}</TableCell>
                    <TableCell>{t('email')}</TableCell>
                    <TableCell align="center">{t('morning')}</TableCell>
                    <TableCell align="center">{t('afternoon')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.reservations.map((reservation) => (
                    <TableRow key={`${reservation.date}-${reservation.email ?? 'unknown'}`}>
                      <TableCell>{formatDate(reservation.date)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{nameOf(reservation)}</span>
                          {/* Only the two that are not a plain personal booking are labelled: a
                              chip on every row would be noise, and these two need explaining. */}
                          {reservation.source === 'GroupReservation' && (
                            <Chip
                              size="small"
                              label={String(t('floorplan-editor-reservations-source-group'))}
                              title={String(t('floorplan-editor-reservations-source-group-explanation'))}
                            />
                          )}
                          {reservation.source === 'CheckIn' && (
                            <Chip
                              size="small"
                              label={String(t('floorplan-editor-reservations-source-check-in'))}
                              title={String(t('floorplan-editor-reservations-source-check-in-explanation'))}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{reservation.email ?? '—'}</TableCell>
                      <DayPartCell
                        held={reservation.morning}
                        label={String(t(reservation.morning
                          ? 'floorplan-editor-reservations-booked'
                          : 'floorplan-editor-reservations-not-booked'))}
                      />
                      <DayPartCell
                        held={reservation.afternoon}
                        label={String(t(reservation.afternoon
                          ? 'floorplan-editor-reservations-booked'
                          : 'floorplan-editor-reservations-not-booked'))}
                      />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {/* Only once there is something to copy: an enabled Copy button over an empty table
            promises a clipboard full of nothing. */}
        {!!data?.reservations.length && (
          <Button
            onClick={copy}
            startIcon={<ContentCopyIcon size={16} />}
            sx={{ mr: 'auto' }}
          >
            {t('floorplan-editor-reservations-copy')}
          </Button>
        )}
        <Button onClick={onClose}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkplaceReservationsDialog;
